import { computed, ref } from 'vue';
import type { ComputedRef } from 'vue';

// Below this the board gives one week the whole width it has. A 340px column beside a label
// column does not fit a phone, and half a week is worse than one: figures sit at the right
// edge of a cell, so a clipped column is a column you cannot read.
const ONE_WEEK_AT_A_TIME_BELOW = 720;
const WIDE_VENDOR_COLUMN_WIDTH = 110;
const NARROW_VENDOR_COLUMN_WIDTH = 72;
const WIDE_WEEK_COLUMN_WIDTH = 340;
// Past this the week stops shrinking and the board scrolls instead — a column narrower than
// this cannot hold a PO number and its quantity on one line.
const MIN_WEEK_COLUMN_WIDTH = 200;
const QUEUE_COLUMN_WIDTH = 300;
const QUEUE_RAIL_WIDTH = 36;

// Module-scoped: the grid measures the space, the column definitions declare the sizes, and
// they are built in different components. The virtualizer places every column by these
// numbers, so the two must never disagree.
const availableWidth = ref(0);
// Module-scoped like availableWidth: the board sets it from the store, the grid and the column
// definitions both read it, and the virtualizer's offsets must agree with the column sizes.
const queueHidden = ref(false);

export interface ScheduleGridMetrics {
  isNarrow: ComputedRef<boolean>;
  vendorColumnWidth: ComputedRef<number>;
  queueColumnWidth: ComputedRef<number>;
  pinnedColumnsWidth: ComputedRef<number>;
  weekColumnWidth: ComputedRef<number>;
  setAvailableWidth: (width: number) => void;
  setQueueHidden: (hidden: boolean) => void;
}

export function useScheduleGridMetrics(): ScheduleGridMetrics {
  // Zero until the grid has measured; the wide layout is the safer thing to render first.
  const isNarrow = computed(() => availableWidth.value > 0 && availableWidth.value < ONE_WEEK_AT_A_TIME_BELOW);
  const vendorColumnWidth = computed(() => (isNarrow.value ? NARROW_VENDOR_COLUMN_WIDTH : WIDE_VENDOR_COLUMN_WIDTH));
  const queueColumnWidth = computed(() => (queueHidden.value ? QUEUE_RAIL_WIDTH : QUEUE_COLUMN_WIDTH));
  const pinnedColumnsWidth = computed(() => vendorColumnWidth.value + queueColumnWidth.value);
  // Subtracts the whole pinned width, not just the vendor column: on a phone the queue starts
  // hidden (Task 5) but still holds the rail's width, which is real space the week column
  // does not get to use either.
  const weekColumnWidth = computed(() => (isNarrow.value ? Math.max(MIN_WEEK_COLUMN_WIDTH, availableWidth.value - pinnedColumnsWidth.value) : WIDE_WEEK_COLUMN_WIDTH));

  function setAvailableWidth(width: number): void {
    availableWidth.value = width;
  }

  function setQueueHidden(hidden: boolean): void {
    queueHidden.value = hidden;
  }

  return { isNarrow, vendorColumnWidth, queueColumnWidth, pinnedColumnsWidth, weekColumnWidth, setAvailableWidth, setQueueHidden };
}
